import * as React from "react";

export function HexBytes(props: { className?: string; bytes: Uint8Array; highlight?: { start: number; length: number } }) {
  const CHAR_WIDTH = 7;

  const getLineWidth = (bytesPerLine: number) => {
    let ret = 0;
    ret += CHAR_WIDTH * 4;
    ret += CHAR_WIDTH * 2;
    ret += CHAR_WIDTH * 3 * bytesPerLine;
    ret += CHAR_WIDTH * 1;
    ret += CHAR_WIDTH * 1 * bytesPerLine;
    return 8 + ret + 8;
  };

  const [bytesPerLine, setBytesPerLine] = React.useState(16);

  const ref = React.useRef<HTMLDivElement | null>(null);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const resize = () => {
      const pr = el.parentElement!.parentElement!.getBoundingClientRect();
      const cr = el.getBoundingClientRect();
      if (pr.width >= 1200) {
        setBytesPerLine(16);
        return;
      }
      for (const bytesPerLine of [16, 12, 8]) {
        const want = getLineWidth(bytesPerLine);
        if (want < cr.width) {
          setBytesPerLine(bytesPerLine);
          return;
        }
      }
    };

    // setTimeout(resize, 0);

    let id = 0;
    const ro = new ResizeObserver(() => {
      clearTimeout(id);
      id = setTimeout(resize, 250);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const lines = [];
  for (let i = 0; i < props.bytes.length; i += bytesPerLine) {
    let idx = i.toString(16);
    while (idx.length < 4) {
      idx = "0" + idx;
    }

    const hex = [];
    const ascii = [];
    for (let j = i; j < i + bytesPerLine && j < props.bytes.length; j++) {
      const x = props.bytes.at(j)!;
      const printable = x >= 0x21 && x <= 0x7e;
      const highlighted = props.highlight && props.highlight.start <= j && j < props.highlight.start + props.highlight.length;
      hex.push(
        <React.Fragment key={hex.length}>
          <span
            className={[
              "text-zinc-300 rounded-sm mx-[-5px] px-[5px] my-[-6px] py-[6px] transition-all duration-[150ms] ease-in-out",
              highlighted ? "bg-zinc-300 text-zinc-800" : props.highlight === undefined ? "bg-transparent text-zinc-300/70" : "bg-transparent text-zinc-700",
            ].join(" ")}
          >
            {(x < 16 ? "0" : "") + x.toString(16)}
          </span>
          <span className="select-none text-zinc-700"> </span>
        </React.Fragment>,
      );
      ascii.push(
        <React.Fragment key={ascii.length}>
          <span className={["select-none", highlighted ? "text-zinc-400" : props.highlight === undefined ? "text-zinc-600/80" : "text-zinc-800/80"].join(" ")}>
            {printable ? String.fromCharCode(x) : "."}
          </span>
        </React.Fragment>,
      );
    }
    while (hex.length < bytesPerLine) {
      hex.push(
        <React.Fragment key={hex.length}>
          <span className="select-none text-zinc-300 rounded-sm mx-[-5px] px-[5px] my-[-6px] py-[6px] transition-all duration-[100ms] ease-in-out">{"  "}</span>
          <span className="select-none text-zinc-700"> </span>
        </React.Fragment>,
      );
    }
    while (ascii.length < bytesPerLine) {
      ascii.push(
        <React.Fragment key={ascii.length}>
          <span className="select-none text-zinc-700"> </span>
        </React.Fragment>,
      );
    }

    lines.push(
      <div key={i} className="">
        <span className="select-none text-zinc-700/50">{idx}</span>
        <span className="select-none text-zinc-700">{"  "}</span>
        <span className="text-zinc-700">{hex}</span>
        <span className="select-none text-zinc-700"> </span>
        <span className="select-none text-zinc-700">{ascii}</span>
      </div>,
    );
  }

  return (
    <div
      className={[
        props.className ?? "",
        "h-fit sticky top-[48px] lg:top-[72px] translate-y-[-1px] lg:translate-y-[0px]",
        "mx-[calc(-16px-8px)] w-[calc(100%+16px+16px+8px+8px)]",
        "sm:mx-[calc(-16px-36px)] sm:w-[calc(100%+16px+16px+36px+36px)]",
        "md:mx-[calc(-16px-72px)] md:w-[calc(100%+16px+16px+72px+72px)]",
        "lg:mx-0 lg:w-[50%] lg:grow",
        "bg-[#09090b]",
      ].join(" ")}
    >
      <div ref={ref} style={{ minWidth: `${getLineWidth(bytesPerLine)}px` }} className="w-full max-w-full h-fit flex lg:justify-end">
        <div className="w-full max-w-full lg:w-fit h-fit bg-zinc-900/50 border-y lg:border-x border-zinc-900 lg:rounded-lg overflow-x-scroll overflow-hide-scrollbar h-fit py-3">
          <div className="px-[16px] sm:px-[calc(16px+36px)] md:px-[calc(16px+72px)] lg:px-[16px] flex flex-col font-mono whitespace-pre text-[10px] text-zinc-400 leading-[20px]">
            {lines}
          </div>
        </div>
      </div>
    </div>
  );
}
